if (!customElements.get("enova-delivery")) {
  const MAX_LOOKAHEAD = 365;

  const toISODate = (date) => {
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
  };

  customElements.define(
    "enova-delivery",
    class EnovaDelivery extends HTMLElement {
      connectedCallback() {
        this.setupDrawer();

        // Rows that show a store count or a fixed text have no date to compute.
        this.target = this.querySelector("[data-delivery-range]");
        if (!this.target) return;

        this.skipWeekdays = (this.dataset.skipWeekdays || "")
          .split(/[^0-6]+/)
          .filter((value) => value !== "")
          .map(Number);
        this.blockedDates = new Set(
          (this.dataset.blockedDates || "")
            .split(/[,;\s]+/)
            .map((value) => value.trim())
            .filter(Boolean)
        );

        this.render();
      }

      setupDrawer() {
        this.drawer = this.querySelector("[data-delivery-drawer]");
        if (!this.drawer) return;

        this.drawerWrapper = this.drawer.querySelector(".m-drawer__wrapper");
        this.drawerContent = this.drawer.querySelector(".m-drawer__content");

        // The product section sits below the sticky header in the stacking order,
        // so the drawer only paints above the page once it hangs off the body.
        document.body.appendChild(this.drawer);

        this.querySelector("[data-delivery-open]")?.addEventListener("click", (event) => {
          event.preventDefault();
          this.openDrawer();
        });

        this.drawer.querySelector("[data-delivery-close]")?.addEventListener("click", (event) => {
          event.preventDefault();
          this.closeDrawer();
        });

        this.drawerWrapper?.addEventListener("click", (event) => {
          if (event.target === this.drawerWrapper) this.closeDrawer();
        });

        document.addEventListener("keydown", (event) => {
          if (event.key === "Escape" && !this.drawerWrapper?.classList.contains("m:hidden")) {
            this.closeDrawer();
          }
        });
      }

      openDrawer() {
        if (window.innerWidth < 770) {
          document.documentElement.classList.add("prevent-scroll");
          this.drawerWrapper.classList.add("mobile");
        }
        this.drawerWrapper.classList.remove("m:hidden");
        requestAnimationFrame(() => {
          this.drawerWrapper.style.setProperty("--m-bg-opacity", "0.5");
          this.drawerContent.style.setProperty("--m-translate-x", "0");
          this.drawerContent.focus();
        });
      }

      disconnectedCallback() {
        this.drawer?.remove();
      }

      closeDrawer() {
        this.drawerWrapper.style.setProperty("--m-bg-opacity", "0");
        this.drawerContent.style.setProperty("--m-translate-x", "100%");
        setTimeout(() => {
          this.drawerWrapper.classList.add("m:hidden");
          this.drawerWrapper.classList.remove("mobile");
          document.documentElement.classList.remove("prevent-scroll");
        }, 300);
      }

      get minDays() {
        return Math.max(parseInt(this.dataset.minDays, 10) || 1, 1);
      }

      get maxDays() {
        return Math.max(parseInt(this.dataset.maxDays, 10) || 1, this.minDays);
      }

      isBlocked(date) {
        return this.skipWeekdays.includes(date.getDay()) || this.blockedDates.has(toISODate(date));
      }

      addWorkingDays(days) {
        const date = new Date();
        date.setHours(12, 0, 0, 0);
        let remaining = days;
        let guard = 0;

        while (remaining > 0 && guard < MAX_LOOKAHEAD) {
          date.setDate(date.getDate() + 1);
          guard += 1;
          if (!this.isBlocked(date)) remaining -= 1;
        }

        return date;
      }

      formatDate(date, precision) {
        const options = { weekday: "long" };
        if (precision !== "weekday") options.day = "numeric";
        if (precision === "month") options.month = "short";

        try {
          return new Intl.DateTimeFormat(this.dataset.locale || undefined, options).format(date);
        } catch (error) {
          return new Intl.DateTimeFormat(undefined, options).format(date);
        }
      }

      render() {
        const from = this.addWorkingDays(this.minDays);
        const to = this.addWorkingDays(this.maxDays);

        // Enova: siempre día, número y mes («viernes, 23 oct»), también en los plazos cortos, para que
        // Lima y provincia se lean igual.
        const precision = "month";

        const single = toISODate(from) === toISODate(to);
        const template = single ? this.dataset.single || "__FROM__" : this.dataset.between || "__FROM__ - __TO__";
        const dates = { __FROM__: this.formatDate(from, precision), __TO__: this.formatDate(to, precision) };

        // Enova: los días van resaltados (<strong class="enova-delivery__date">) para que el plazo
        // se lea de un vistazo. Se arma con nodos de texto, sin innerHTML.
        const fragment = document.createDocumentFragment();
        template.split(/(__FROM__|__TO__)/).forEach((part) => {
          if (!part) return;
          if (dates[part] !== undefined) {
            const strong = document.createElement("strong");
            strong.className = "enova-delivery__date";
            strong.textContent = dates[part];
            fragment.appendChild(strong);
          } else {
            fragment.appendChild(document.createTextNode(part));
          }
        });

        this.target.replaceChildren(fragment);
        this.target.hidden = false;
      }
    }
  );
}
